import { useGetWorkspaceQuery } from '@carboncopyco/data';
import { FolderIcon, SharedFolderIcon } from '@carboncopyco/icons';
import { LeftArrowIllustration } from '@carboncopyco/illustrations';
import type { Folder } from '@carboncopyco/types';
import {
  Breadcrumbs,
  ColorHex,
  TextPageHeader,
  TooltipBubble,
} from '@carboncopyco/ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWorkspacePageProps } from '../../WorkspacePageContext';

interface FolderPageHeaderProps {
  folder: Folder;
  archived?: boolean;
}

export const FolderPageHeader = ({
  folder,
  archived,
}: FolderPageHeaderProps) => {
  const { workspaceId, organizationId } = useWorkspacePageProps();
  const { search = '' } = useLocation();
  const { rootPath } = useWorkspacePageProps();
  const navigate = useNavigate();
  const { data: workspace } = useGetWorkspaceQuery({
    organizationId: organizationId,
    id: workspaceId,
  });

  const links = [
    {
      label: workspace?.workspaceName,
      onClick: () => navigate(`${rootPath}${search}`),
    },
  ];

  const getBreadcrumbsLinks = () => {
    if (archived) {
      links.push({
        label: 'Archive',
        onClick: () => navigate(`${rootPath}/archive`),
      });
    }
    return links;
  };

  return (
    <div className="flex flex-col justify-start gap-3">
      {workspace && (
        <Breadcrumbs
          links={getBreadcrumbsLinks()}
          current={folder?.name}
        />
      )}
      <div className="flex items-center">
        <div
          className="hover:cursor-pointer pr-2"
          onClick={() =>
            navigate(`${rootPath}${archived ? '/archive' : ''}${search}`)
          }
        >
          <LeftArrowIllustration />
        </div>
        <div className="pr-4">
          {folder?.readOnly ? (
            <SharedFolderIcon
              size={36}
              color={ColorHex.natural600}
            />
          ) : (
            <FolderIcon
              size={36}
              color={ColorHex.natural600}
            />
          )}
        </div>
        <TooltipBubble
          title={folder?.name}
          placement="top-start"
        >
          <div className="max-w-[800px] mr-10 truncate">
            <TextPageHeader
              label={folder?.name}
              color="text-ds-natural-600"
              ellipsis
            />
          </div>
        </TooltipBubble>
      </div>
    </div>
  );
};
